import AuthMessage from "@/components/AuthMessage";
import RoundedButton from "@/components/RoundedButton";
import clsx from "clsx";
import React, { forwardRef } from "react";
import TextareaAutosize from "react-autosize-textarea";
import { Check } from "react-feather";
import ReactHtmlParser from "react-html-parser";

const baseFieldClassName = clsx(
  "block w-full rounded-md border-0 px-4 py-3 font-normal text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-sky-500 sm:leading-6",
);

const Form = ({
  method = "post",
  action = "/",
  children,
  onSubmit,
  buttonLabel = "Submit",
  busyButtonLabel = "Submitting...",
  flashMessage = { message: null, type: null },
  size = "base",
  infoMessage = "",
  isBusy = false,
}) => {
  return (
    <form
      method={method}
      action={action}
      onSubmit={onSubmit}
      className={clsx(
        "Form",
        "overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 md:col-span-2 lg:rounded-xl",
      )}
    >
      <div className={clsx("Form__content", "")}>
        {!!flashMessage && !!flashMessage.message && (
          <div
            className={clsx(
              " px-6 py-6 text-lg  sm:p-8 [&>a]:underline [&>a]:decoration-sky-500/25 [&>a]:decoration-1 [&>a]:underline-offset-4",
              {
                "bg-sky-50 text-sky-500": flashMessage.type === "success",
                "bg-red-50 text-red-500": flashMessage.type === "error",
              },
            )}
          >
            {ReactHtmlParser(flashMessage.message)}
          </div>
        )}

        {!!infoMessage && !flashMessage?.message && (
          <div
            className={clsx(
              "bg-gray-50 px-6 py-6 text-lg hover:text-gray-700 text-gray-600 sm:p-8 [&_a]:link-lg [&_a]:text-current [&_a]:decoration-gray-300",
            )}
            dangerouslySetInnerHTML={{ __html: infoMessage }}
          ></div>
        )}

        <div className={clsx("Form__children", "space-y-4 px-6 py-6 sm:p-8")}>
          {children}
        </div>

        <div
          className={clsx("Form__actions", "flex bg-gray-50 border-t", {
            "px-6 py-6 sm:p-6": size === "base",
            "justify-end px-4 py-4 sm:p-8": size === "sm",
          })}
        >
          <RoundedButton
            type="submit"
            disabled={isBusy}
            icon={Check}
            isLoading={isBusy}
            size={size}
          >
            {isBusy ? busyButtonLabel : buttonLabel}
          </RoundedButton>
        </div>
      </div>
    </form>
  );
};

const Field = ({ name, label, error, children }) => {
  return (
    <div className={clsx("")}>
      <label
        htmlFor={`${name}`}
        className={clsx(
          "Form__label",
          "block font-medium leading-6 text-gray-600",
        )}
      >
        <div className={clsx("pb-2 leading-none")}>{label}</div>
        {children}
        {!!error && (
          <div
            className={clsx(
              "Form__error",
              "pt-2 font-normal text-red-700 [&>a]:underline [&>a]:decoration-red-200 [&>a]:decoration-1 [&>a]:underline-offset-2",
            )}
          >
            {ReactHtmlParser(error)}
          </div>
        )}
      </label>
    </div>
  );
};

Field.displayName = "Field";

Form.Field = Field;

type InputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & { label: string; error: string };

const InputField = forwardRef<HTMLInputElement>((props: InputProps, ref) => {
  return (
    <Field name={props.name} label={props.label} error={props.error}>
      <input
        {...props}
        className={clsx("form-input", baseFieldClassName)}
        ref={ref}
        id={props.name}
      />
    </Field>
  );
});

InputField.displayName = "InputField";

Form.InputField = InputField;

type TextareaProps = React.DetailedHTMLProps<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
> & { label: string; error: string };

const TextareaField = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props: TextareaProps, ref) => {
    return (
      <Field name={props.name} label={props.label} error={props.error}>
        <TextareaAutosize
          className={clsx("form-textarea", baseFieldClassName)}
          rows={2}
          {...props}
          ref={ref}
        />
      </Field>
    );
  },
);

TextareaField.displayName = "TextareaField";

Form.TextareaField = TextareaField;

type SelectProps = React.DetailedHTMLProps<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
> & {
  label: string;
  error: string;
  options: [{ value: string; label: string }];
};

const SelectField = forwardRef<HTMLSelectElement, SelectProps>(
  (props: SelectProps, ref) => {
    return (
      <Field name={props.name} label={props.label} error={props.error}>
        <select className={clsx("form-select")} ref={ref} {...props}>
          {!!props.options &&
            props.options.map((option) => {
              return (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              );
            })}
        </select>
      </Field>
    );
  },
);

SelectField.displayName = "SelectField";

Form.SelectField = SelectField;

export default Form;
